import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Input, Select, DatePicker, Tag, message, Empty, Tooltip
} from 'antd';
import {
  PlusOutlined, UnorderedListOutlined, ImportOutlined, SaveOutlined,
  DeleteOutlined, ReloadOutlined, ScissorOutlined, InboxOutlined, ClearOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { cuttingPlannerApi } from './cuttingPlannerApi';
import {
  hasPermission, PERM, summarizeItems, formatThickness, thicknessChip, isCep, stockDisplayName
} from './cuttingPlannerHelpers';
import { buildPieceColumns, PIECE_GRID_SCROLL_X } from './cuttingPlannerColumns';

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}

// A compact figure tile for the commit strip. SqMt leads (accent) — it's the
// metric the planner reads first; pieces are reference.
function StatTile({ label, value, accent }) {
  return (
    <div className="flex flex-col leading-none gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">{label}</span>
      <span className={`tabular-nums font-bold ${accent ? 'text-brand-600 text-[19px]' : 'text-ink-800 text-[16px]'}`}>{value}</span>
    </div>
  );
}

// Map an importItems row OR a planItems row into the uniform grid shape.
function toGridRow(x) {
  return {
    sourcePieceId: x.sourcePieceId,
    barcode: x.barcode || null,
    workOrderNo: x.sourceWorkOrderNo || x.workOrderNo || null,
    workOrderDate: x.workOrderDate || null,
    customerName: x.customerName || null,
    proformaNo: x.proformaNo || null,
    itemDesc: x.itemDesc || null,
    thickness: x.thickness != null ? Number(x.thickness) : null,
    glassType: x.glassType || null,
    stockName: x.stockName || null,
    widthMm: x.widthMm != null ? Number(x.widthMm) : null,
    heightMm: x.heightMm != null ? Number(x.heightMm) : null,
    qty: x.qty != null ? Number(x.qty) : 1,
    sqft: x.sqft != null ? Number(x.sqft) : null,
    sqmt: x.sqmt != null ? Number(x.sqmt) : null,
    edgeFinish: x.edgeFinish || (isCep(x.itemDesc) ? 'CEP' : null),
    fabInfo: x.fabInfo || null,
    pieceNo: x.pieceNo || null,
    remarks: x.remarks || null
  };
}

export default function CuttingPlannerTab({ me, editTarget, onSaved }) {
  const canManage = hasPermission(me, PERM.MANAGE);

  const [optNo, setOptNo] = useState(null);
  const [planDate, setPlanDate] = useState(dayjs());
  const [editingPlanId, setEditingPlanId] = useState(null);

  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersPageSize, setOrdersPageSize] = useState(10);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [selectedWoNos, setSelectedWoNos] = useState([]);
  const [woSearch, setWoSearch] = useState('');
  const [woSearchInput, setWoSearchInput] = useState('');

  const [stockOptions, setStockOptions] = useState([]);
  const [stockKey, setStockKey] = useState(null);
  const [descLoading, setDescLoading] = useState(false);

  const [grid, setGrid] = useState([]);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const res = (await cuttingPlannerApi.pendingOrders({
        page: ordersPage, pageSize: ordersPageSize, search: woSearch.trim() || undefined
      })) || {};
      setOrders(res.rows || []);
      setOrdersTotal(Number(res.total) || 0);
    } catch (err) { message.error(backendError(err, 'Could not load pending work orders.')); }
    finally { setOrdersLoading(false); }
  }, [ordersPage, ordersPageSize, woSearch]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  const newPlan = useCallback(async () => {
    try {
      const { optNo: n } = await cuttingPlannerApi.nextOptNo();
      setOptNo(n);
      setPlanDate(dayjs());
      setEditingPlanId(null);
      setSelectedWoNos([]); setStockOptions([]); setStockKey(null); setGrid([]);
      loadOrders();
    } catch (err) { message.error(backendError(err, 'Could not start a new plan.')); }
  }, [loadOrders]);

  // Reopen a saved plan for editing (from the tracker).
  useEffect(() => {
    if (!editTarget || !editTarget.planId) return;
    (async () => {
      try {
        const data = await cuttingPlannerApi.planItems(editTarget.planId);
        setEditingPlanId(data.plan.id);
        setOptNo(data.plan.optNo);
        setPlanDate(data.plan.planDate ? dayjs(data.plan.planDate) : dayjs());
        setGrid((data.items || []).map(toGridRow));
        setSelectedWoNos([]); setStockOptions([]); setStockKey(null);
        await loadOrders();
      } catch (err) { message.error(backendError(err, 'Could not open the plan for editing.')); }
    })();
  }, [editTarget, loadOrders]);

  const getItemList = useCallback(async () => {
    if (!selectedWoNos.length) return;
    setDescLoading(true);
    try {
      const res = (await cuttingPlannerApi.stockGroups(selectedWoNos)) || { rows: [] };
      const list = res.rows || [];
      // { value: stock item id (or NO_STOCK sentinel), label: stock name, pieces }
      setStockOptions(list.map((g) => ({ label: g.label, value: g.value, pieces: g.pieces })));
      if (!list.length) message.info('No cuttable stock in the selected work orders.');
    } catch (err) { message.error(backendError(err, 'Could not load stock list.')); }
    finally { setDescLoading(false); }
  }, [selectedWoNos]);

  const importPieces = useCallback(async () => {
    if (!selectedWoNos.length || !stockKey) return;
    setImporting(true);
    try {
      const res = (await cuttingPlannerApi.importItems(selectedWoNos, stockKey)) || { rows: [] };
      const incoming = (res.rows || []).map(toGridRow);
      setGrid((prev) => {
        const seen = new Set(prev.map((r) => r.sourcePieceId));
        const added = incoming.filter((r) => !seen.has(r.sourcePieceId));
        if (!added.length) message.info('All those pieces are already in the grid.');
        else message.success(`Imported ${added.length} piece${added.length === 1 ? '' : 's'}.`);
        return [...prev, ...added];
      });
    } catch (err) { message.error(backendError(err, 'Could not import pieces.')); }
    finally { setImporting(false); }
  }, [selectedWoNos, stockKey]);

  const removePiece = useCallback((id) => setGrid((prev) => prev.filter((r) => r.sourcePieceId !== id)), []);

  const summary = useMemo(() => summarizeItems(grid), [grid]);

  const savePlan = useCallback(async () => {
    if (optNo == null) { message.warning('Start a new plan first.'); return; }
    if (!planDate) { message.warning('Pick a plan date.'); return; }
    if (!grid.length) { message.warning('Add at least one piece.'); return; }
    setSaving(true);
    try {
      const out = await cuttingPlannerApi.save({
        planId: editingPlanId || undefined,
        optNo,
        planDate: planDate.format('YYYY-MM-DD'),
        thickness: summary.thickness,
        glassType: grid.find((r) => r.glassType)?.glassType || undefined,
        items: grid.map((r) => ({ sourcePieceId: r.sourcePieceId }))
      });
      message.success(`Plan ${out.planNo} saved · ${out.totalPcs} pcs · ${Number(out.totalSqmt).toFixed(3)} sqmt.`);
      setGrid([]); setOptNo(null); setEditingPlanId(null);
      setSelectedWoNos([]); setStockOptions([]); setStockKey(null);
      loadOrders();
      if (onSaved) onSaved();
    } catch (err) { message.error(backendError(err, 'Could not save the plan.')); }
    finally { setSaving(false); }
  }, [optNo, planDate, grid, editingPlanId, summary, loadOrders, onSaved]);

  const commitWoSearch = useCallback((v) => { setWoSearch((v ?? woSearchInput).trim()); setOrdersPage(1); }, [woSearchInput]);

  // Two columns only, so the queue fits 360px with NO inner horizontal scroll:
  // a 2-line Work Order cell (no. + date · customer) and an available/total
  // fraction where the available count leads in emerald.
  const orderColumns = [
    { title: 'Work Order', key: 'wo',
      render: (_v, r) => (
        <div className="min-w-0">
          <div className="font-semibold text-ink-800 text-[12px] truncate">{r.workOrderNo}</div>
          <div className="text-[11px] text-ink-400 truncate">
            {r.workOrderDate ? dayjs(r.workOrderDate).format('DD MMM') : '—'}{r.customer ? ` · ${r.customer}` : ''}
          </div>
        </div>
      ) },
    { title: 'Avail', key: 'a', width: 74, align: 'right',
      render: (_v, r) => (
        <span className="inline-flex items-baseline gap-0.5 tabular-nums">
          <span className={`text-[15px] font-bold ${r.availablePcs ? 'text-emerald-600' : 'text-ink-300'}`}>{r.availablePcs}</span>
          <span className="text-[11px] text-ink-400">/{r.totalPcs}</span>
        </span>
      ) }
  ];

  const gridColumns = [
    ...buildPieceColumns(),
    { title: '', key: 'x', width: 44, align: 'center', fixed: 'right',
      render: (_v, r) => (
        <Tooltip title="Remove">
          <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removePiece(r.sourcePieceId)} disabled={!canManage} />
        </Tooltip>
      ) }
  ];

  return (
    <div className="flex flex-col gap-3">
      {/* Command bar — three clear zones (identity · progress · actions) with
          room to breathe. The stepper is deliberately quiet so it reads as a
          secondary progress hint, not a peer of the title/actions. */}
      <div className="rounded-xl border border-surface-200 bg-surface-50 px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
            <ScissorOutlined className="text-brand-600 text-[16px]" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="text-[14px] font-semibold text-ink-800">Cutting Optimization Planner</span>
            <span className="text-[11.5px] text-ink-400 mt-0.5">
              {optNo ? `OPT-${optNo}` : 'New plan'}{planDate ? ` · ${planDate.format('DD MMM YYYY')}` : ''}{editingPlanId ? ' · Editing' : ''}
            </span>
          </div>
        </div>
        {/* Numbered stepper — muted; lights up softly as the flow advances */}
        <div className="hidden xl:flex items-center gap-3 pl-6 border-l border-surface-200">
          {[
            { n: 1, label: 'Select WOs', done: selectedWoNos.length > 0 },
            { n: 2, label: 'Get Items', done: stockOptions.length > 0 },
            { n: 3, label: 'Import', done: grid.length > 0 },
            { n: 4, label: 'Save', done: false }
          ].map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`h-5 w-5 rounded-full flex items-center justify-center text-[10px] font-semibold transition-colors ${s.done ? 'bg-brand-500 text-white' : 'bg-white border border-surface-200 text-ink-300'}`}>{s.n}</div>
              <span className={`text-[11px] whitespace-nowrap ${s.done ? 'text-ink-600 font-medium' : 'text-ink-400'}`}>{s.label}</span>
              {i < 3 ? <span className="text-surface-300 text-[11px] ml-1">›</span> : null}
            </div>
          ))}
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2.5">
          <DatePicker value={planDate} onChange={(d) => setPlanDate(d || dayjs())} allowClear={false} format="DD MMM YYYY" size="small" disabled={!canManage} />
          <Button type="primary" icon={<PlusOutlined />} size="small" onClick={newPlan} disabled={!canManage}>New Plan</Button>
          <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={loadOrders} /></Tooltip>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-4 items-start">
        {/* Left — pending work orders queue (server-paginated) */}
        <div className="rounded-xl border border-surface-200 bg-white shadow-card flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-100 flex flex-col gap-2.5">
            <div className="flex items-center gap-2">
              <InboxOutlined className="text-brand-500 text-[14px]" />
              <span className="text-[13px] font-semibold text-ink-800">Open Work Orders</span>
              {selectedWoNos.length
                ? <Tag color="blue" className="!m-0 tabular-nums">{selectedWoNos.length} selected</Tag>
                : <span className="text-[11px] text-ink-400 tabular-nums">{ordersTotal} open</span>}
            </div>
            <Input.Search allowClear size="small" placeholder="Search WO no. or customer" value={woSearchInput}
              onChange={(e) => { setWoSearchInput(e.target.value); if (!e.target.value) commitWoSearch(''); }}
              onSearch={(v) => commitWoSearch(v)} />
          </div>
          <Table
            className="cutting-grid" size="small" rowKey="workOrderNo" loading={ordersLoading} columns={orderColumns}
            dataSource={orders} scroll={{ y: 360 }}
            pagination={{
              current: ordersPage, pageSize: ordersPageSize, total: ordersTotal, size: 'small',
              showSizeChanger: true, pageSizeOptions: ['10', '25', '50'],
              showTotal: (t) => `${t} WO${t === 1 ? '' : 's'}`
            }}
            onChange={(pag) => {
              if (pag.pageSize !== ordersPageSize) { setOrdersPageSize(pag.pageSize); setOrdersPage(1); }
              else setOrdersPage(pag.current);
            }}
            rowSelection={{
              selectedRowKeys: selectedWoNos, onChange: setSelectedWoNos,
              preserveSelectedRowKeys: true,
              getCheckboxProps: () => ({ disabled: !canManage })
            }}
            locale={{ emptyText: <Empty description="No open work orders with cuttable pieces." image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
          />
          {/* Add-pieces footer — guided two-step */}
          <div className="px-4 py-3 border-t border-surface-100 bg-surface-50 flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Add pieces</span>
              {stockOptions.length ? (
                <span className="text-[11px] text-ink-400 tabular-nums">{stockOptions.length} stock{stockOptions.length === 1 ? '' : 's'}</span>
              ) : null}
            </div>
            <Button block size="small" icon={<UnorderedListOutlined />} onClick={getItemList} loading={descLoading}
              disabled={!canManage || !selectedWoNos.length}>
              Get Item List{selectedWoNos.length ? ` · ${selectedWoNos.length} WO${selectedWoNos.length === 1 ? '' : 's'}` : ''}
            </Button>
            <div className="flex items-stretch gap-2">
              <Select size="small" showSearch placeholder="Choose stock…" className="!flex-1 !min-w-0"
                value={stockKey} onChange={setStockKey} options={stockOptions}
                optionFilterProp="label" disabled={!stockOptions.length}
                optionRender={(opt) => (
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate">{opt.data.label}</span>
                    {opt.data.pieces != null
                      ? <span className="text-[11px] text-ink-400 tabular-nums shrink-0">{opt.data.pieces} pc{opt.data.pieces === 1 ? '' : 's'}</span>
                      : null}
                  </div>
                )} />
              <Button size="small" type="primary" icon={<ImportOutlined />} onClick={importPieces}
                loading={importing} disabled={!canManage || !stockKey}>Import</Button>
            </div>
          </div>
        </div>

        {/* Right — plan pieces tray (min-w-0 so the wide table scrolls INSIDE,
            never pushing the page past the viewport) */}
        <div className="rounded-xl border border-surface-200 bg-white shadow-card flex flex-col min-w-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-surface-100 flex items-center gap-2">
            <ScissorOutlined className="text-brand-500 text-[14px]" />
            <span className="text-[13px] font-semibold text-ink-800">Plan Pieces</span>
            {grid.length ? <Tag color="blue" className="!m-0 tabular-nums">{grid.length}</Tag> : null}
            <div className="flex-1" />
            {grid.length ? (
              <Tooltip title="Remove all pieces from the plan">
                <Button size="small" type="text" danger icon={<ClearOutlined />} onClick={() => setGrid([])} disabled={!canManage}>Clear</Button>
              </Tooltip>
            ) : null}
          </div>
          <Table
            className="cutting-grid" size="small" rowKey="sourcePieceId" columns={gridColumns} dataSource={grid}
            scroll={{ x: PIECE_GRID_SCROLL_X, y: 380 }}
            pagination={grid.length > 100
              ? { pageSize: 100, size: 'small', showSizeChanger: true, pageSizeOptions: ['100', '200', '500'], showTotal: (t) => `${t} pieces` }
              : false}
            locale={{ emptyText: (
              <div className="py-9 flex flex-col items-center gap-2 text-center">
                <div className="h-11 w-11 rounded-full bg-brand-50 flex items-center justify-center">
                  <ImportOutlined className="text-brand-500 text-[18px]" />
                </div>
                <div className="text-[13px] font-medium text-ink-600">No pieces yet</div>
                <div className="text-[12px] text-ink-400 max-w-[300px]">
                  Select work orders on the left, choose a stock, then <b className="text-ink-500">Import</b> to build the plan.
                </div>
              </div>
            ) }}
          />
          {/* Commit strip — SqMt leads; Save shows the OPT it will write. */}
          <div className="px-4 py-3.5 border-t border-surface-100 bg-surface-50 flex flex-wrap items-center gap-x-7 gap-y-3">
            <StatTile label="SqMt" value={summary.totalSqmt.toFixed(3)} accent />
            <StatTile label="SqFt" value={summary.totalSqft.toFixed(3)} />
            <StatTile label="Pieces" value={summary.totalPcs} />
            <div className="h-8 w-px bg-surface-200 hidden sm:block" />
            <div className="flex flex-col leading-none gap-1">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">Thickness</span>
              {summary.thickness != null
                ? (() => { const c = thicknessChip(summary.thickness); return (
                    <span className="inline-block self-start rounded px-1.5 leading-[20px] text-[12px] font-semibold tabular-nums"
                      style={{ background: c.bg, color: c.fg }}>{formatThickness(summary.thickness)}</span>
                  ); })()
                : <span className="text-[13px] text-ink-300">mixed</span>}
            </div>
            {summary.stockName ? (
              <div className="flex flex-col leading-none gap-1 min-w-0 max-w-[220px]">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-400">Stock</span>
                <span className="text-[13px] text-ink-700 truncate" title={stockDisplayName(summary.stockName)}>{stockDisplayName(summary.stockName)}</span>
              </div>
            ) : null}
            <div className="flex-1" />
            <Button type="primary" icon={<SaveOutlined />} onClick={savePlan} loading={saving}
              disabled={!canManage || !grid.length || optNo == null}>
              {editingPlanId ? 'Update Plan' : 'Save Plan'}{optNo != null ? ` · OPT-${optNo}` : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
